import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { User, LogOut, LayoutDashboard, Hotel, Bus, Car, Menu, X, Code, FileText, Handshake } from "lucide-react";
import { useState } from "react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdminBuild = process.env.REACT_APP_MODE === "admin";

  // Hide nav on the landing page — it redirects to https://safarsetu.com so
  // we never want to flash the old green nav during the 80ms before redirect.
  if (location.pathname === "/") return null;

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const getDashboardPath = () => {
    if (!user) return "/auth";
    if (user.role === "admin") return "/admin";
    if (user.role === "host") return "/host";
    return "/dashboard";
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/40" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to={isAdminBuild ? "/auth" : "/"} className="flex items-center gap-2" data-testid="navbar-logo">
            <div className="w-8 h-8 bg-[#2C5F2D] rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm font-['Outfit']">S</span>
            </div>
            <span className="text-xl font-bold text-[#1A231C] font-['Outfit'] tracking-tight">SafarSetu{isAdminBuild ? " Admin" : ""}</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {!isAdminBuild && (<>
            <button onClick={() => navigate("/search?type=hotel")} className="text-[#5C6B60] hover:text-[#2C5F2D] transition-colors text-sm font-medium flex items-center gap-1.5 bg-transparent" data-testid="nav-hotels">
              <Hotel className="w-4 h-4" /> Hotels
            </button>
            <button onClick={() => navigate("/search?type=bus")} className="text-[#5C6B60] hover:text-[#2C5F2D] transition-colors text-sm font-medium flex items-center gap-1.5 bg-transparent" data-testid="nav-buses">
              <Bus className="w-4 h-4" /> Buses
            </button>
            <button onClick={() => navigate("/search?type=cab")} className="text-[#5C6B60] hover:text-[#2C5F2D] transition-colors text-sm font-medium flex items-center gap-1.5 bg-transparent" data-testid="nav-cabs">
              <Car className="w-4 h-4" /> Cabs
            </button>
            <Link to="/blog" className="text-[#5C6B60] hover:text-[#2C5F2D] transition-colors text-sm font-medium flex items-center gap-1.5" data-testid="nav-blog">
              <FileText className="w-4 h-4" /> Blog
            </Link>
            <button onClick={() => navigate("/auth?tab=register&role=host")} className="text-[#E07A5F] hover:text-[#C96A50] transition-colors text-sm font-semibold flex items-center gap-1.5 bg-transparent" data-testid="nav-partner">
              <Handshake className="w-4 h-4" /> List as Partner
            </button>
            </>)}
          </div>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="rounded-full border-[#E5E3DB] gap-2 hover:border-[#2C5F2D]/30" data-testid="user-menu-trigger">
                    <div className="w-7 h-7 rounded-full bg-[#2C5F2D] flex items-center justify-center">
                      <span className="text-white text-xs font-medium">{user.name?.charAt(0).toUpperCase()}</span>
                    </div>
                    <span className="text-sm text-[#1A231C]">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate(getDashboardPath())} data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  {!isAdminBuild && (
                  <DropdownMenuItem onClick={() => navigate("/developers")} data-testid="menu-api-docs">
                    <Code className="w-4 h-4 mr-2" /> API Docs
                  </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : !isAdminBuild ? (
              <>
                <Button variant="outline" className="rounded-full border-[#E5E3DB] text-[#1A231C] hover:border-[#2C5F2D]/30" onClick={() => navigate("/auth")} data-testid="nav-login-btn">
                  Login
                </Button>
                <Button className="rounded-full bg-[#2C5F2D] text-white hover:bg-[#214A22]" onClick={() => navigate("/auth?tab=register")} data-testid="nav-register-btn">
                  Register
                </Button>
              </>
            ) : null}
          </div>

          <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="w-5 h-5 text-[#1A231C]" /> : <Menu className="w-5 h-5 text-[#1A231C]" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden py-4 border-t border-[#E5E3DB] space-y-3">
            {!isAdminBuild && (<>
            <Link to="/search?type=hotel" className="block py-2 text-[#5C6B60] hover:text-[#2C5F2D] text-sm font-medium" onClick={() => setMobileOpen(false)}>Hotels</Link>
            <Link to="/search?type=bus" className="block py-2 text-[#5C6B60] hover:text-[#2C5F2D] text-sm font-medium" onClick={() => setMobileOpen(false)}>Buses</Link>
            <Link to="/search?type=cab" className="block py-2 text-[#5C6B60] hover:text-[#2C5F2D] text-sm font-medium" onClick={() => setMobileOpen(false)}>Cabs</Link>
            <Link to="/blog" className="block py-2 text-[#5C6B60] hover:text-[#2C5F2D] text-sm font-medium" onClick={() => setMobileOpen(false)}>Blog</Link>
            <button onClick={() => { navigate("/auth?tab=register&role=host"); setMobileOpen(false); }} className="block py-2 text-[#E07A5F] font-semibold text-sm">List as Partner</button>
            </>)}
            {user ? (
              <>
                <Link to={getDashboardPath()} className="block py-2 text-[#2C5F2D] font-medium text-sm" onClick={() => setMobileOpen(false)}>Dashboard</Link>
                <button onClick={() => { handleLogout(); setMobileOpen(false); }} className="block py-2 text-red-600 text-sm font-medium">Logout</button>
              </>
            ) : !isAdminBuild ? (
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="rounded-full flex-1" onClick={() => { navigate("/auth"); setMobileOpen(false); }}>Login</Button>
                <Button className="rounded-full bg-[#2C5F2D] flex-1" onClick={() => { navigate("/auth?tab=register"); setMobileOpen(false); }}>Register</Button>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </nav>
  );
}
